import { type FilterSet } from '../helpers/filters';
import { type GeoInfo, geoInfoToUrlParams } from '../services/geo-info';
import { buildApiUrl } from '../helpers/urls';
import DistanceUnit from './location/distance-units';
import { type PaginationDataSet } from '../types/pagination';
import { type ProjectTile } from '../types/project';

export interface ProjectsSearchRequest {
  businessRegistryUrl: string;
  geoInfo: GeoInfo;
  sectors: string[];
  trades: string[];
  classifications: string[];
  query: string;
  page: number;
  pageSize: number;
  radius: number;
  unit?: DistanceUnit;
  sort?: string;
  loggingContext: string;
  onlyOfficeAddress?: boolean;
  projectDetails?: string[];
  fundingType?: string[];
  companyId?: number;
}

export interface SearchResultData {
  results: ProjectTile[];
  count: number;
  finalRadius?: number;
}

export const fetchSearch = async (
  location?: GeoInfo,
  query?: string,
  filters?: FilterSet,
  paginationData?: PaginationDataSet,
  radius?: number,
  isStateSearch?: boolean
): Promise<SearchResultData> => {
  const params = geoInfoToUrlParams(location);

  if (query) {
    params.push(`q=${encodeURIComponent(query)}`);
  }

  const sectors = [];
  const fundingTypes = [];
  const trades = [];
  const classifications = [];
  const projectDetails = [];
  if (filters) {
    // Add other filter handling here.
    if (filters.constructionSector && filters.constructionSector.length > 0) {
      filters.constructionSector.forEach((filter) => {
        sectors.push(filter.key);
      });
      params.push(`sector=${sectors.join(',')}`);
    }
    if (filters.fundingType && filters.fundingType.length > 0) {
      filters.fundingType.forEach((filter) => {
        fundingTypes.push(filter.key);
      });
      params.push(`fundingType=${fundingTypes.join(',')}`);
    }
    if (filters.trade && filters.trade.length > 0) {
      filters.trade.forEach((filter) => {
        trades.push(filter.key);
      });
      params.push(`trade=${trades.join(',')}`);
    }
    if (filters.classifications && filters.classifications.length > 0) {
      filters.classifications.forEach((filter) => {
        classifications.push(filter.key);
      });
      params.push(`classifications=${classifications.join(',')}`);
    }
    if (filters.sortBy) {
      params.push(`sort=${filters.sortBy.key}`);
    }
  }

  if (radius) {
    params.push(`radius=${radius}`);
  }

  if (filters?.projectDetails && filters.projectDetails.length > 0) {
    filters.projectDetails.forEach((filter) => {
      projectDetails.push(filter.key);
    });
    params.push(`projectDetails=${projectDetails.join(',')}`);
  }

  if (paginationData) {
    params.push(
      `page=${paginationData.page}&pageSize=${paginationData.pageSize}`
    );
  }
  if (isStateSearch) {
    params.push(`isStateSearch=true`);
  }

  const url = buildApiUrl(`projectsSearch?${params.join('&')}`);
  let result;
  let json;
  if (url.length < 12000) {
    result = await fetch(url);
    json = await result.json();
  } else {
    json = {
      results: [],
      count: 0,
      finalRadius: 0,
    };
  }

  /* istanbul ignore next */
  return {
    results: json.results || [],
    count: json.count,
    finalRadius: json.finalRadius,
  };
};
